import { useEffect, useState } from "react";
import { load as loadCashfree } from "@cashfreepayments/cashfree-js";
import { toast } from "sonner";
import { Globe, Loader2, Plus, Trash2, Server, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import api, { extractErr } from "@/lib/api";
import { useMerchantAuth } from "@/lib/auth";
import { useNavigate, useSearchParams } from "react-router-dom";

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function AdminDomain() {
  const { tenant, refresh } = useMerchantAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [dns, setDns] = useState(null);
  const [ns, setNs] = useState(["", ""]);
  const [busy, setBusy] = useState(false);
  const [rec, setRec] = useState({ type: "A", host: "@", value: "", ttl: 7200 });
  const storeEnabled = tenant?.store_enabled !== false && tenant?.account_kind !== "domain_only";

  const load = async () => {
    try {
      const { data } = await api.get("/merchant/domain");
      setInfo(data);
      const names = data.domain?.nameservers || [];
      setNs([names[0] || "", names[1] || "", ...(names.slice(2))].slice(0, 4));
      if (data.domain?.status === "registered") {
        try {
          const d = await api.get("/merchant/domain/dns");
          setDns(d.data);
        } catch {
          setDns({ records: [], domain: data.domain.domain });
        }
      }
    } catch (e) {
      toast.error(extractErr(e));
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const oid = sp.get("cf_order_id");
    if (!oid || !oid.startsWith("dup_")) return;
    (async () => {
      try {
        const { data } = await api.get(`/merchant/domain/upgrade/verify/${oid}`);
        if (data.active) {
          toast.success("Website unlocked — pick a theme");
          await refresh();
          nav("/admin/setup-theme", { replace: true });
          return;
        }
        toast.error("Payment not confirmed yet");
      } catch (e) {
        toast.error(extractErr(e));
      } finally {
        sp.delete("cf_order_id");
        setSp(sp, { replace: true });
      }
    })();
  }, [sp]);

  const saveNs = async () => {
    setBusy(true);
    try {
      await api.post("/merchant/domain/nameservers", { nameservers: ns.filter(Boolean) });
      toast.success("Nameservers updated");
      load();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setBusy(false); }
  };

  const addRec = async () => {
    setBusy(true);
    try {
      await api.post("/merchant/domain/dns", { action: "add", ...rec });
      toast.success("Record added");
      setRec({ ...rec, value: "" });
      load();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setBusy(false); }
  };

  const delRec = async (r) => {
    setBusy(true);
    try {
      await api.post("/merchant/domain/dns", { action: "delete", type: r.type, host: r.host, value: r.value });
      toast.success("Record deleted");
      load();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setBusy(false); }
  };

  const retry = async () => {
    setBusy(true);
    try {
      await api.post("/merchant/domain/retry-register");
      toast.success("Registration retried");
      load();
    } catch (e) { toast.error(extractErr(e)); }
    finally { setBusy(false); }
  };

  const upgrade = async (planId) => {
    setBusy(true);
    try {
      const { data } = await api.post("/merchant/domain/upgrade", { plan_id: planId });
      if (data.free && data.active) {
        await refresh();
        nav("/admin/setup-theme");
        return;
      }
      const cashfree = await loadCashfree({ mode: data.sdk_mode || "sandbox" });
      await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
    } catch (e) {
      toast.error(extractErr(e));
      setBusy(false);
    }
  };

  const domain = info?.domain;
  const plans = info?.plans || {};

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-domain-page">
      <div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><Globe className="w-7 h-7" /> My Domain</h1>
        <p className="text-slate-500 text-sm mt-1">DNS records and nameservers for the domain you bought with us.</p>
      </div>

      {!storeEnabled ? (
        <Card className="p-5 border-orange-200 bg-orange-50" data-testid="domain-upsell-banner">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-[#FF6B00] mt-0.5" />
            <div className="flex-1">
              <div className="font-bold text-stone-900">More than 10,000+ people choose to build a website with us</div>
              <p className="text-sm text-stone-600 mt-1">Add a storefront to this domain. After payment you’ll pick a theme and unlock the full merchant dashboard.</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button disabled={busy} onClick={() => upgrade("website")} data-testid="domain-upgrade-website" className="bg-[#FF6B00] hover:bg-[#e86000] text-white">
                  Add Website {rupee(plans.website?.price || 1999)}/year
                </Button>
                <Button disabled={busy} variant="outline" onClick={() => upgrade("website_app")} data-testid="domain-upgrade-app">
                  Website + App {rupee(plans.website_app?.price || 5998)}/year
                </Button>
                <Button disabled={busy} variant="outline" onClick={() => upgrade("website_app_ios")} data-testid="domain-upgrade-ios">
                  iOS + Android + Website {rupee(plans.website_app_ios?.price || 19999)}/year
                </Button>
              </div>
            </div>
          </div>
        </Card>
      ) : null}

      {!domain ? (
        <Card className="p-6 text-slate-500">No domain on this account yet. Search and buy one at /domain.</Card>
      ) : (
        <>
          <Card className="p-5">
            <div className="text-xs uppercase tracking-wider text-slate-400">Registered domain</div>
            <div className="mt-1 text-2xl font-bold">{domain.domain}</div>
            <div className="mt-1 text-sm text-slate-500">Status: {domain.status}{domain.error ? ` — ${domain.error}` : ""}</div>
            {domain.register_price != null || domain.renew_price != null ? (
              <div className="mt-2 text-sm text-slate-600">
                First year {rupee(domain.register_price)} · renew {rupee(domain.renew_price ?? domain.register_price)}/yr
                {domain.premium ? " · premium" : ""}
              </div>
            ) : null}
            <p className="mt-3 text-xs text-slate-500">
              Store connect uses CNAME <span className="font-mono">www → shops.merastartup.in</span> (same as Settings → Domain).
              Apex <span className="font-mono">@</span> CNAME is tried; if the registry blocks it we forward non-www to www. Registrar default nameservers stay in place — we do not add A records.
            </p>
            {domain.status === "failed" ? (
              <Button className="mt-3" disabled={busy} onClick={retry} data-testid="domain-retry">Retry registration</Button>
            ) : null}
          </Card>

          <Card className="p-5 space-y-3">
            <div className="flex items-center gap-2 font-semibold"><Server className="w-4 h-4" /> Nameservers</div>
            {(ns.length ? ns : ["", ""]).map((v, i) => (
              <div key={i}>
                <Label className="text-xs">NS {i + 1}</Label>
                <Input className="mt-1" value={v} onChange={(e) => setNs(ns.map((x, j) => j === i ? e.target.value : x))} />
              </div>
            ))}
            <Button disabled={busy} onClick={saveNs} data-testid="domain-save-ns">Save nameservers</Button>
          </Card>

          <Card className="p-5 space-y-3">
            <div className="font-semibold">DNS records</div>
            <div className="grid sm:grid-cols-5 gap-2">
              <select className="h-10 rounded-md border px-2" value={rec.type} onChange={(e) => setRec({ ...rec, type: e.target.value })}>
                {["A", "AAAA", "CNAME", "MX", "TXT"].map((t) => <option key={t}>{t}</option>)}
              </select>
              <Input placeholder="host" value={rec.host} onChange={(e) => setRec({ ...rec, host: e.target.value })} />
              <Input className="sm:col-span-2" placeholder="value" value={rec.value} onChange={(e) => setRec({ ...rec, value: e.target.value })} />
              <Button disabled={busy || !rec.value} onClick={addRec} data-testid="domain-add-dns"><Plus className="w-4 h-4 mr-1" /> Add</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-500"><th className="py-2">Type</th><th>Host</th><th>Value</th><th /></tr></thead>
                <tbody>
                  {(dns?.records || []).map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="py-2 font-mono">{r.type}</td>
                      <td className="font-mono">{r.host}</td>
                      <td className="font-mono break-all">{r.value}</td>
                      <td>
                        <button type="button" onClick={() => delRec(r)} className="text-red-600 p-1" aria-label="Delete record"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {busy ? <div className="text-xs text-slate-400 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Working…</div> : null}
          </Card>
        </>
      )}
    </div>
  );
}
